import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Empty, Flex, Skeleton, Spin, Typography } from "antd";
import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { DAP_FIELDS, NoteType, SOAP_FIELDS } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getNotesDetails, handleGenerateTranscriptOfNote } from "../../service/notes.service";
import { setNotesDetails, setRecordingId } from "../../slices/notesSlice";
import {
  DiagnosticCodeStatus,
  ProceduralCodeStatus,
  type TDiagnosticCode,
  type TNotes,
  type TProceduralCode,
} from "../../types/index.type";

// ─── Shared label style ────────────────────────────────────────────────────────

const sectionTitle = (label: string) => (
  <Typography.Text strong style={{ fontSize: 14 }}>
    {label}
  </Typography.Text>
);

// ─── Note content ──────────────────────────────────────────────────────────────

const NoteField = ({ label, text }: { label: string; text: string | null | undefined }) => {
  if (!text) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <Typography.Text
        style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.06em" }}
      >
        {label}
      </Typography.Text>
      <div style={{ whiteSpace: "pre-wrap", marginTop: 6, lineHeight: 1.7, color: "#111827" }}>{text}</div>
    </div>
  );
};

const NoteContent = ({ note }: { note: TNotes }) => {
  const { note_type } = note;
  if (note_type === NoteType.SOAP)
    return (
      <>
        {SOAP_FIELDS.map((f) => (
          // @ts-ignore
          <NoteField key={f.object} label={f.title} text={note[f.object]} />
        ))}
      </>
    );
  if (note_type === NoteType.DAP)
    return (
      <>
        {DAP_FIELDS.map((f) => (
          // @ts-ignore
          <NoteField key={f.object} label={f.title} text={note[f.object]} />
        ))}
      </>
    );
  if (note_type === NoteType.Intake) return <NoteField label="Intake" text={note.intake_note} />;
  if (note_type === NoteType.EAP_INTAKE) return <NoteField label="EAP Intake" text={note.eap_intake_note} />;
  if (note_type === NoteType.PSYCHIATRIC_INTAKE)
    return <NoteField label="Psychiatric Intake" text={note.psych_intake_note} />;
  if (note_type === NoteType.EMDR) return <NoteField label="EMDR" text={note.emdr_note} />;
  if (note_type === NoteType.SOL_PSYCH_INTAKE)
    return <NoteField label="Psychiatric Intake" text={note.sol_psych_intake_note} />;
  if (note_type === NoteType.PSYCHIATRIC_FOLLOW_UP)
    return <NoteField label="Psychiatric Followup" text={note.sol_psych_followup_note} />;
  if (note_type === NoteType.THERAPY_INTAKE)
    return <NoteField label="Therapy Intake" text={note.sol_therapy_intake_note} />;
  if (note_type === NoteType.THERAPY_FOLLOW_UP_DAP || note_type === NoteType.THERAPY_FOLLOW_UP_SOAP)
    return <NoteField label="Therapy Followup" text={note.sol_therapy_followup_note} />;
  if (note_type === NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP)
    return <NoteField label="Psychiatric Followup" text={note.psych_followup_note} />;
  return <NoteField label={note.note_type_info?.name ?? "Note"} text={note.document} />;
};

const NoteContentOrEmpty = ({ note }: { note: TNotes }) => {
  const content = <NoteContent note={note} />;
  // NoteField returns null for empty text, so if nothing renders we show a fallback
  const hasAnyText = [
    note.subject,
    note.objective,
    note.assessment,
    note.plan,
    note.data,
    note.intake_note,
    note.eap_intake_note,
    note.psych_intake_note,
    note.emdr_note,
    note.sol_psych_intake_note,
    note.sol_psych_followup_note,
    note.sol_therapy_intake_note,
    note.sol_therapy_followup_note,
    note.psych_followup_note,
    note.document,
  ].some(Boolean);
  if (!hasAnyText) return <Empty description="Note content not available." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return content;
};

// ─── Smart Coding (CPT) ────────────────────────────────────────────────────────

const SmartCodingCard = ({ note }: { note: TNotes }) => {
  const { procedural_codes, procedural_code_status } = note;

  let body: ReactNode;
  if (procedural_code_status === ProceduralCodeStatus.PENDING) {
    body = (
      <Flex align="center" gap={8}>
        <LoadingOutlined />
        <Typography.Text style={{ color: "#6b7280" }}>Generating billing codes…</Typography.Text>
      </Flex>
    );
  } else if (
    procedural_code_status === ProceduralCodeStatus.NOT_PROCESSED ||
    procedural_code_status === ProceduralCodeStatus.FAILED
  ) {
    body = <Typography.Text style={{ color: "#9ca3af" }}>No billing codes generated for this note.</Typography.Text>;
  } else if (!procedural_codes || procedural_codes.length === 0) {
    body = <Typography.Text style={{ color: "#9ca3af" }}>No billing codes found.</Typography.Text>;
  } else {
    body = procedural_codes.map((code: TProceduralCode, i) => (
      <div
        key={code.id}
        style={{
          paddingBottom: 16,
          marginBottom: i < procedural_codes.length - 1 ? 16 : 0,
          borderBottom: i < procedural_codes.length - 1 ? "1px solid #f0f0f0" : "none",
        }}
      >
        <Typography.Text strong style={{ fontSize: 13 }}>
          {code.code}
        </Typography.Text>
        {code.short_description && <div style={{ color: "#374151", marginTop: 2 }}>{code.short_description}</div>}
        {code.rationale && <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>{code.rationale}</div>}
      </div>
    ));
  }

  return (
    <Card title={sectionTitle("Smart Coding")} style={{ marginBottom: 24 }}>
      {body}
    </Card>
  );
};

// ─── Diagnosis Codes (ICD-10) ──────────────────────────────────────────────────

const DiagnosisCodesCard = ({ note }: { note: TNotes }) => {
  const { diagnostic_codes, diagnostic_code_status } = note;

  let body: ReactNode;
  if (diagnostic_code_status === DiagnosticCodeStatus.PENDING) {
    body = (
      <Flex align="center" gap={8}>
        <LoadingOutlined />
        <Typography.Text style={{ color: "#6b7280" }}>Generating diagnosis codes…</Typography.Text>
      </Flex>
    );
  } else if (
    diagnostic_code_status === DiagnosticCodeStatus.NOT_PROCESSED ||
    diagnostic_code_status === DiagnosticCodeStatus.FAILED
  ) {
    body = <Typography.Text style={{ color: "#9ca3af" }}>No diagnosis codes generated for this note.</Typography.Text>;
  } else if (!diagnostic_codes || diagnostic_codes.length === 0) {
    body = <Typography.Text style={{ color: "#9ca3af" }}>No diagnosis codes found.</Typography.Text>;
  } else {
    body = diagnostic_codes.map((code: TDiagnosticCode, i) => (
      <div
        key={code.id}
        style={{
          paddingBottom: 16,
          marginBottom: i < diagnostic_codes.length - 1 ? 16 : 0,
          borderBottom: i < diagnostic_codes.length - 1 ? "1px solid #f0f0f0" : "none",
        }}
      >
        <Typography.Text strong style={{ fontSize: 13 }}>
          {code.code}
        </Typography.Text>
        {code.short_description && <div style={{ color: "#374151", marginTop: 2 }}>{code.short_description}</div>}
        {code.rationale && <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>{code.rationale}</div>}
      </div>
    ));
  }

  return (
    <Card title={sectionTitle("Diagnosis Codes")} style={{ marginBottom: 24 }}>
      {body}
    </Card>
  );
};

// ─── Transcript ────────────────────────────────────────────────────────────────

const TranscriptPanel = () => {
  const { notesDetails } = useAppSelector((state) => state.notes);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!notesDetails?.id) return;
    setLoading(true);
    setContent("");
    handleGenerateTranscriptOfNote(notesDetails.id)
      .then((r) => setContent(r.data.transcript ?? ""))
      .catch(() => setContent(""))
      .finally(() => setLoading(false));
  }, [notesDetails?.id]);

  if (loading) {
    return (
      <>
        <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
          <LoadingOutlined />
          <Typography.Text style={{ color: "#6b7280" }}>Loading transcript…</Typography.Text>
        </Flex>
        <Skeleton active />
      </>
    );
  }

  return (
    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.8, color: "#111827", fontSize: 14 }}>
      {content || <Typography.Text style={{ color: "#9ca3af" }}>No transcript available.</Typography.Text>}
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const ComplianceNoteReview = () => {
  const { userId, noteId } = useParams<{ userId: string; noteId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { userInfo } = useAppSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const numericNoteId = Number(noteId);
  const numericUserId = Number(userId);

  useEffect(() => {
    if (!numericNoteId) return;
    setLoading(true);
    setError(false);
    getNotesDetails(numericNoteId)
      .then((r) => {
        dispatch(setNotesDetails(r.data));
        dispatch(setRecordingId(numericNoteId));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      dispatch(setNotesDetails(null));
    };
  }, [numericNoteId, dispatch]);

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }
  if (!numericNoteId || !numericUserId) {
    return <Navigate to="/compliance/clinicians" replace />;
  }

  const noteTitle = notesDetails?.title || `Note #${numericNoteId}`;

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh", textAlign: "left", padding: "40px" }}>
      <Flex align="center" gap={8} style={{ marginBottom: 20 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/compliance/clinicians/${userId}`)}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance
                </a>
              ),
            },
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance/clinicians");
                  }}
                >
                  Clinicians
                </a>
              ),
            },
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/compliance/clinicians/${userId}`);
                  }}
                >
                  Clinician
                </a>
              ),
            },
            { title: noteTitle },
          ]}
        />
      </Flex>

      {loading ? (
        <Flex justify="center" style={{ padding: 80 }}>
          <Spin size="large" />
        </Flex>
      ) : error || !notesDetails ? (
        <Flex vertical align="center" gap={12} style={{ padding: 80 }}>
          <Typography.Text style={{ color: "#6b7280" }}>Failed to load note.</Typography.Text>
          <Button onClick={() => navigate(`/compliance/clinicians/${userId}`)}>Back to clinician</Button>
        </Flex>
      ) : (
        <>
          <div style={{ marginBottom: 24 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {noteTitle}
            </Typography.Title>
            {notesDetails.client_name && (
              <Typography.Text style={{ color: "#6b7280" }}>{notesDetails.client_name}</Typography.Text>
            )}
          </div>

          <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
            {/* Left column — note + codes */}
            <div style={{ flex: "1 1 0", minWidth: 0, paddingBottom: 80 }}>
              <Card title={sectionTitle("Note")} style={{ marginBottom: 24 }}>
                <NoteContentOrEmpty note={notesDetails} />
              </Card>

              {notesDetails.has_transcript && (
                <>
                  <SmartCodingCard note={notesDetails} />
                  <DiagnosisCodesCard note={notesDetails} />
                </>
              )}
            </div>

            {/* Right column — transcript, sticky */}
            <div
              style={{
                flex: "0 0 500px",
                position: "sticky",
                top: 40,
                maxHeight: "calc(100vh - 80px)",
                overflowY: "auto",
              }}
            >
              <Card title={sectionTitle("Transcript")}>
                <TranscriptPanel />
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceNoteReview;
